// Settings → Tables: the floor set up by area with the same drawn tables the Tables tab
// shows, the chosen table edited beside it, and a starter for a restaurant with none yet.
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  IconButton,
  IconTile,
  Input,
  Modal,
  Select,
  cx,
  useToast,
} from '@ims/ui'
import {
  ChevronRight,
  Minus,
  Pencil,
  PicnicTable,
  Plus,
  QrCode,
  Trash2,
  UtensilsCrossed,
} from '@ims/ui/icons'
import { type ReactNode, useState } from 'react'
import { useEntitlements, useSettings } from '../hooks.ts'
import { sameName } from '../lib/pick.ts'
import { deleteOp, insertOp, newDiningArea, newDiningTable, updateOp } from '../lib/rows.ts'
import { useWide } from '../lib/wide.ts'
import { useCan, useWorkspace } from '../workspace.tsx'
import type { DiningArea, DiningTable, Floor } from './floor.ts'
import { TableQrDialog, useQrOrdering } from './guest-requests.tsx'
import { useWrite } from './order-hooks.ts'
import { TableShape, chairsPerSide } from './table-shape.tsx'

const FLOOR =
  'rounded-card border border-border bg-surface bg-[radial-gradient(var(--color-soft)_1.2px,transparent_1.2px)] bg-[length:18px_18px] shadow-card'
const MAX_SEATS = 30

/** A whole number with − and + beside it; the box can be typed in too. */
function NumberStepper({
  label,
  value,
  min = 1,
  max,
  disabled,
  compact,
  onChange,
}: {
  label: string
  value: number
  min?: number
  max: number
  disabled?: boolean
  /** Smaller, for a row of a list. */
  compact?: boolean
  onChange: (value: number) => void
}) {
  const set = (next: number) => {
    const n = Math.min(max, Math.max(min, Math.round(next)))
    if (Number.isFinite(n) && n !== value) onChange(n)
  }
  const side = cx(
    'flex shrink-0 items-center justify-center bg-soft text-ink hover:bg-border disabled:opacity-40',
    compact ? 'size-9' : 'size-10',
  )
  return (
    <div
      role="group"
      aria-label={label}
      className="flex w-fit items-center overflow-hidden rounded-xl border border-border bg-surface"
    >
      <button
        type="button"
        aria-label={`${label}: one fewer`}
        disabled={disabled || value <= min}
        onClick={() => set(value - 1)}
        className={side}
      >
        <Minus className="size-4" />
      </button>
      <input
        aria-label={label}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        disabled={disabled}
        value={value}
        onChange={(e) => e.target.value && set(Number(e.target.value))}
        className={cx(
          'tabular border-0 bg-transparent p-0 text-center font-bold text-ink [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none',
          compact ? 'w-9 text-sm' : 'w-14',
        )}
      />
      <button
        type="button"
        aria-label={`${label}: one more`}
        disabled={disabled || value >= max}
        onClick={() => set(value + 1)}
        className={side}
      >
        <Plus className="size-4" />
      </button>
    </div>
  )
}

/** A value edited in place and saved when the field is left. */
function SaveOnLeave({
  label,
  value,
  autoFocus,
  onSave,
  onDone,
}: {
  label: string
  value: string
  autoFocus?: boolean
  onSave: (value: string) => void
  onDone?: () => void
}) {
  const [text, setText] = useState(value)
  const [last, setLast] = useState(value)
  if (value !== last) {
    setLast(value)
    setText(value)
  }
  return (
    <Input
      aria-label={label}
      autoFocus={autoFocus}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      onBlur={() => {
        const next = text.trim()
        if (!next) setText(value)
        else if (next !== value) onSave(next)
        onDone?.()
      }}
    />
  )
}

/** A small table for a list row: the same drawing, a few pixels high. */
function MiniTable({ seats }: { seats: number | null }) {
  const per = chairsPerSide(seats)
  const chairs = (
    <span className="flex justify-center gap-[3px]">
      {Array.from({ length: per }, (_, i) => (
        <span key={i} className="h-[3px] w-1.5 rounded-full bg-neutral/60" />
      ))}
    </span>
  )
  return (
    <span aria-hidden className="flex w-11 shrink-0 flex-col items-center gap-0.5">
      {chairs}
      <span
        className="h-4 rounded-[5px] border-[1.5px] border-neutral/60"
        style={{ width: `${18 + per * 6}px` }}
      />
      {chairs}
    </span>
  )
}

/** The Area lists' last choice: make a new area here (docs/plans/add-here.md). */
const NEW_AREA = '__new'

export function TableSetup({ floor }: { floor: Floor }) {
  const { current } = useWorkspace()
  const canEdit = useCan('manager')
  const limit = useEntitlements()?.limits?.max_tables ?? null
  const { write, busy } = useWrite()
  const toast = useToast()
  const settings = useSettings()
  const qrOrdering = useQrOrdering(settings)
  const wide = useWide()
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [several, setSeveral] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [newArea, setNewArea] = useState<string | null>(null)
  const [removing, setRemoving] = useState<DiningTable | DiningArea | null>(null)
  const [qrFor, setQrFor] = useState<DiningTable | null>(null)
  // "+ New area…" chosen for a table: the name being typed for it.
  const [areaFor, setAreaFor] = useState<{ tableId: string; name: string } | null>(null)

  const ctx = { tenantId: current!.tenant_id }
  const { tables, areas } = floor
  const room = limit === null ? Infinity : Math.max(0, limit - tables.length)
  const seatTotal = tables.reduce((sum, t) => sum + (t.seats ?? 0), 0)
  // Beside the floor a table is always open; below that width only once one is tapped.
  const picked = tables.find((t) => t.id === pickedId) ?? (wide ? tables[0] : undefined)

  /** "T6", "T7"… continuing after the highest table number in use. */
  const tableOps = (count: number, seats: number | null, areaId: string | null) => {
    const highest = tables.reduce(
      (max, t) => Math.max(max, Number(/(\d+)\s*$/.exec(t.name)?.[1]) || 0),
      0,
    )
    return Array.from({ length: count }, (_, i) =>
      insertOp(
        'dining_tables',
        newDiningTable(ctx, {
          name: `T${highest + i + 1}`,
          area_id: areaId,
          seats,
          sort_order: highest + i + 1,
        }),
      ),
    )
  }
  /** The area with this name, or a new one whose insert goes with whatever uses it. */
  const areaNamed = (name: string) => {
    const existing = areas.find((a) => sameName(a.name, name))
    const area = existing ? null : newDiningArea(ctx, { name, sort_order: areas.length })
    return {
      id: existing?.id ?? area!.id,
      ops: area ? [insertOp('dining_areas', area)] : [],
    }
  }
  const addOne = (areaId: string | null) => {
    if (room < 1) {
      toast.error('Your plan has no room for another table. A bigger plan has more.')
      return
    }
    void write(() => tableOps(1, null, areaId))
  }
  const isTable = (row: DiningTable | DiningArea): row is DiningTable => 'is_active' in row
  const remove = (row: DiningTable | DiningArea) => {
    if (isTable(row) && floor.orders.some((o) => o.order.table_id === row.id)) {
      toast.error(`${row.name} has an open order. Check it out or move it first.`)
      return Promise.resolve(false)
    }
    if (!isTable(row) && tables.some((t) => t.area_id === row.id)) {
      toast.error(`Move or remove the tables in ${row.name} first.`)
      return Promise.resolve(false)
    }
    return write(() => [deleteOp(isTable(row) ? 'dining_tables' : 'dining_areas', row.id)])
  }
  const setSeats = (table: DiningTable, seats: number) =>
    void write(() => [updateOp('dining_tables', table, { seats })])

  const confirm = (
    <ConfirmDialog
      open={removing !== null}
      title={`Remove ${removing?.name ?? ''}?`}
      message="Past orders and their invoices are kept."
      confirmLabel="Remove"
      danger
      busy={busy}
      onClose={() => setRemoving(null)}
      onConfirm={() => removing && void remove(removing).then(() => setRemoving(null))}
    />
  )

  if (tables.length === 0)
    return canEdit ? (
      <Starter
        room={room}
        limit={limit}
        busy={busy}
        onCreate={(count, seats, areaName) => {
          const existing = areas.find((a) => a.name.toLowerCase() === areaName.toLowerCase())
          const area =
            areaName && !existing
              ? newDiningArea(ctx, { name: areaName, sort_order: areas.length })
              : null
          const areaId = area?.id ?? existing?.id ?? null
          return write(() => [
            ...(area ? [insertOp('dining_areas', area)] : []),
            ...tableOps(count, seats, areaId),
          ])
        }}
        onOne={() => addOne(null)}
      />
    ) : (
      <Card className="p-0">
        <EmptyState
          icon={<UtensilsCrossed className="size-6" />}
          title="No tables yet"
          description="A manager adds the tables here."
        />
      </Card>
    )

  const panels = [
    ...areas.map((a) => ({
      area: a as DiningArea | null,
      tables: tables.filter((t) => t.area_id === a.id),
    })),
    {
      area: null as DiningArea | null,
      tables: tables.filter((t) => !areas.some((a) => a.id === t.area_id)),
    },
  ].filter((p) => p.area !== null || p.tables.length > 0)

  const newAreaName = picked && areaFor?.tableId === picked.id ? areaFor.name : null
  const editor = picked && canEdit && (
    <div className="grid gap-4">
      <Field label="Name" hint="What staff and the kitchen ticket call it.">
        {() => (
          <SaveOnLeave
            label={`Name of ${picked.name}`}
            value={picked.name}
            onSave={(name) => void write(() => [updateOp('dining_tables', picked, { name })])}
          />
        )}
      </Field>
      <div>
        <span className="mb-1.5 block text-sm font-medium text-muted">Seats</span>
        <NumberStepper
          label={`Seats at ${picked.name}`}
          value={picked.seats ?? 4}
          max={MAX_SEATS}
          disabled={busy}
          onChange={(seats) => setSeats(picked, seats)}
        />
      </div>
      <Field label="Area">
        {() => (
          <Select
            aria-label={`Area of ${picked.name}`}
            value={newAreaName !== null ? NEW_AREA : (picked.area_id ?? '')}
            onChange={(e) => {
              if (e.target.value === NEW_AREA) {
                setAreaFor({ tableId: picked.id, name: '' })
                return
              }
              setAreaFor(null)
              void write(() => [
                updateOp('dining_tables', picked, { area_id: e.target.value || null }),
              ])
            }}
          >
            <option value="">No area</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
            <option value={NEW_AREA}>+ New area…</option>
          </Select>
        )}
      </Field>
      {newAreaName !== null && (
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            const name = newAreaName.trim()
            if (!name) return
            void write(() => {
              const area = areaNamed(name)
              return [...area.ops, updateOp('dining_tables', picked, { area_id: area.id })]
            }).then((ok) => ok && setAreaFor(null))
          }}
        >
          <Field label="New area name" className="min-w-0 flex-1">
            {(id) => (
              <Input
                id={id}
                autoFocus
                maxLength={60}
                value={newAreaName}
                onChange={(e) => setAreaFor({ tableId: picked.id, name: e.target.value })}
                placeholder="e.g. Rooftop"
              />
            )}
          </Field>
          <Button type="submit" disabled={!newAreaName.trim()} loading={busy}>
            Add
          </Button>
        </form>
      )}
      {qrOrdering && (
        <div className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
          <IconTile area="neutral">
            <QrCode className="size-[18px]" />
          </IconTile>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">Table QR code</span>
            <span className="block text-xs text-muted">Guests scan it to order</span>
          </span>
          <Button size="sm" variant="secondary" onClick={() => setQrFor(picked)}>
            Show
          </Button>
        </div>
      )}
      <button
        type="button"
        aria-label={`Remove ${picked.name}`}
        onClick={() => setRemoving(picked)}
        className="flex w-fit items-center gap-2 rounded-lg px-1 py-1.5 text-sm font-semibold text-danger hover:underline"
      >
        <Trash2 className="size-4" />
        Remove this table
      </button>
    </div>
  )

  return (
    <>
      <Card className="flex flex-wrap items-center gap-x-5 gap-y-3 p-4">
        <div className="min-w-48 flex-1">
          <div className="flex justify-between gap-3 text-sm">
            <b className="font-semibold text-ink">
              {limit === null
                ? `${tables.length} tables.`
                : `${tables.length} of ${limit} tables in your plan.`}
            </b>
            <span className="text-muted">{seatTotal} seats</span>
          </div>
          {limit !== null && (
            <div className="mt-1.5 h-1.5 rounded-full bg-soft">
              <div
                className="h-full rounded-full bg-kitchen"
                style={{ width: `${Math.min(100, Math.round((tables.length / limit) * 100))}%` }}
              />
            </div>
          )}
        </div>
        {canEdit && (
          <Button
            variant="secondary"
            icon={<Plus className="size-4" />}
            onClick={() => setSeveral(true)}
          >
            Add several tables
          </Button>
        )}
      </Card>

      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1 space-y-4">
          {panels.map(({ area, tables: mine }) => {
            const name = area?.name ?? (areas.length > 0 ? 'No area' : 'Tables')
            const seats = mine.reduce((sum, t) => sum + (t.seats ?? 0), 0)
            return (
              <section key={area?.id ?? 'none'} aria-label={name} className={FLOOR}>
                <div className="flex min-h-11 items-center gap-1.5 pt-1.5 pr-2 pl-4">
                  {area && renaming === area.id ? (
                    <div className="w-44">
                      <SaveOnLeave
                        label={`Name of ${area.name}`}
                        value={area.name}
                        autoFocus
                        onSave={(next) =>
                          void write(() => [updateOp('dining_areas', area, { name: next })])
                        }
                        onDone={() => setRenaming(null)}
                      />
                    </div>
                  ) : (
                    <h3 className="text-sm font-bold text-ink">{name}</h3>
                  )}
                  {area && canEdit && renaming !== area.id && (
                    <IconButton label={`Rename ${area.name}`} onClick={() => setRenaming(area.id)}>
                      <Pencil className="size-4" />
                    </IconButton>
                  )}
                  <span className="text-xs text-muted">
                    {mine.length} {mine.length === 1 ? 'table' : 'tables'}
                    {seats > 0 && ` · ${seats} seats`}
                  </span>
                  {area && canEdit && (
                    <span className="ml-auto">
                      <IconButton label={`Remove ${area.name}`} onClick={() => setRemoving(area)}>
                        <Trash2 className="size-4" />
                      </IconButton>
                    </span>
                  )}
                </div>
                {wide ? (
                  <ul className="flex flex-wrap items-center gap-x-6 gap-y-5 px-5 pt-3 pb-5">
                    {mine.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          aria-label={`${t.name}, ${t.seats ?? 'no'} seats`}
                          aria-pressed={picked?.id === t.id}
                          disabled={!canEdit}
                          onClick={() => setPickedId(t.id)}
                          className="rounded-card focus-visible:outline-none"
                        >
                          <TableShape seats={t.seats} tone="plain" selected={picked?.id === t.id}>
                            <span className="max-w-full truncate text-[15px] font-bold text-ink">
                              {t.name}
                            </span>
                            <span className="text-xs text-muted">
                              {t.seats ? `${t.seats} seats` : 'Seats not set'}
                            </span>
                          </TableShape>
                        </button>
                      </li>
                    ))}
                    {canEdit && (
                      <li>
                        <button
                          type="button"
                          aria-label={`Add table to ${name}`}
                          onClick={() => addOne(area?.id ?? null)}
                          disabled={busy}
                          className="flex h-[5.5rem] w-32 flex-col items-center justify-center gap-0.5 rounded-card border-[1.5px] border-dashed border-primary/40 bg-primary-light/40 text-sm font-bold text-primary hover:bg-primary-light"
                        >
                          <Plus className="size-[18px]" />
                          Add table
                        </button>
                      </li>
                    )}
                  </ul>
                ) : (
                  <ul className="mx-3 mt-1 mb-3 divide-y divide-soft overflow-hidden rounded-xl border border-border bg-surface">
                    {mine.map((t) => (
                      <li
                        key={t.id}
                        className="flex min-h-14 items-center gap-2.5 py-1.5 pr-1.5 pl-2"
                      >
                        <MiniTable seats={t.seats} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-bold text-ink">{t.name}</span>
                          <span className="block text-xs text-muted">
                            {t.seats ? `${t.seats} seats` : 'Not set'}
                          </span>
                        </span>
                        {canEdit && (
                          <>
                            <NumberStepper
                              compact
                              label={`Seats at ${t.name}`}
                              value={t.seats ?? 4}
                              max={MAX_SEATS}
                              disabled={busy}
                              onChange={(seats) => setSeats(t, seats)}
                            />
                            <IconButton label={`Edit ${t.name}`} onClick={() => setPickedId(t.id)}>
                              <ChevronRight className="size-4" />
                            </IconButton>
                          </>
                        )}
                      </li>
                    ))}
                    {canEdit && (
                      <li>
                        <button
                          type="button"
                          aria-label={`Add table to ${name}`}
                          onClick={() => addOne(area?.id ?? null)}
                          disabled={busy}
                          className="flex min-h-12 w-full items-center justify-center gap-1.5 bg-primary-light/40 text-sm font-bold text-primary"
                        >
                          <Plus className="size-4" />
                          Add table
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              </section>
            )
          })}
          {canEdit &&
            (newArea === null ? (
              <button
                type="button"
                onClick={() => setNewArea('')}
                className="flex min-h-12 w-full items-center justify-center gap-2 rounded-card border-[1.5px] border-dashed border-border px-3 text-sm font-semibold text-muted hover:border-primary/40 hover:text-ink"
              >
                <Plus className="size-4" />
                Add an area
                <span className="font-normal max-sm:hidden">(rooftop, garden, a private room)</span>
              </button>
            ) : (
              <form
                className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface p-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  const name = newArea.trim()
                  if (!name) return
                  void write(() => [
                    insertOp(
                      'dining_areas',
                      newDiningArea(ctx, { name, sort_order: areas.length }),
                    ),
                  ]).then((ok) => ok && setNewArea(null))
                }}
              >
                <Field label="New area" className="min-w-40 flex-1">
                  {(id) => (
                    <Input
                      id={id}
                      autoFocus
                      value={newArea}
                      onChange={(e) => setNewArea(e.target.value)}
                      placeholder="e.g. Rooftop"
                    />
                  )}
                </Field>
                <Button type="submit" disabled={!newArea.trim()} loading={busy}>
                  Add area
                </Button>
                <Button type="button" variant="ghost" onClick={() => setNewArea(null)}>
                  Cancel
                </Button>
              </form>
            ))}
        </div>
        {wide && picked && canEdit && (
          <Card
            className="sticky top-0 w-72 shrink-0 p-0"
            role="complementary"
            aria-label={`${picked.name}, selected table`}
          >
            <div className="flex items-center gap-2.5 border-b border-border px-4 py-3.5">
              <IconTile area="kitchen">
                <PicnicTable className="size-[18px]" />
              </IconTile>
              <div className="min-w-0">
                <h3 className="truncate font-bold text-ink">{picked.name}</h3>
                <p className="text-xs text-muted">
                  {areas.find((a) => a.id === picked.area_id)?.name ?? 'No area'} · saved as you
                  change it
                </p>
              </div>
            </div>
            <div className="p-4">{editor}</div>
          </Card>
        )}
      </div>

      {!wide && (
        <Modal
          open={Boolean(pickedId && picked && canEdit)}
          title={picked?.name ?? ''}
          onClose={() => setPickedId(null)}
        >
          {editor}
        </Modal>
      )}
      {several && (
        <SeveralDialog
          areas={areas}
          room={room}
          busy={busy}
          onClose={() => setSeveral(false)}
          onAdd={(count, seats, areaId, newArea) =>
            write(() => {
              const area = newArea ? areaNamed(newArea) : null
              return [...(area?.ops ?? []), ...tableOps(count, seats, area?.id ?? areaId)]
            }).then((ok) => ok && setSeveral(false))
          }
        />
      )}
      {qrFor && settings && (
        <TableQrDialog table={qrFor} settings={settings} onClose={() => setQrFor(null)} />
      )}
      {confirm}
    </>
  )
}

function RoomNote({ count, room }: { count: number; room: number }) {
  if (count <= room) return null
  return (
    <p className="text-sm text-warning">
      Your plan has room for {room} more {room === 1 ? 'table' : 'tables'}. A bigger plan has more
      (Settings → Subscription).
    </p>
  )
}

function SeveralDialog({
  areas,
  room,
  busy,
  onClose,
  onAdd,
}: {
  areas: DiningArea[]
  room: number
  busy: boolean
  onClose: () => void
  /** `newArea` is the name of an area to make for them. */
  onAdd: (count: number, seats: number, areaId: string | null, newArea?: string) => void
}) {
  const [count, setCount] = useState(5)
  const [seats, setSeats] = useState(4)
  const [areaId, setAreaId] = useState('')
  const [newArea, setNewArea] = useState('')
  const making = areaId === NEW_AREA
  return (
    <Modal
      open
      title="Add several tables"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={count > room || (making && !newArea.trim())}
            onClick={() =>
              making
                ? onAdd(count, seats, null, newArea.trim())
                : onAdd(count, seats, areaId || null)
            }
          >
            Add {count} {count === 1 ? 'table' : 'tables'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap gap-4">
          <Labelled label="How many tables?">
            <NumberStepper label="How many tables?" value={count} max={100} onChange={setCount} />
          </Labelled>
          <Labelled label="Seats at each">
            <NumberStepper
              label="Seats at each"
              value={seats}
              max={MAX_SEATS}
              onChange={setSeats}
            />
          </Labelled>
        </div>
        <Field label="Area">
          {(id) => (
            <Select id={id} value={areaId} onChange={(e) => setAreaId(e.target.value)}>
              <option value="">No area</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
              <option value={NEW_AREA}>+ New area…</option>
            </Select>
          )}
        </Field>
        {making && (
          <Field label="New area name">
            {(id) => (
              <Input
                id={id}
                autoFocus
                maxLength={60}
                value={newArea}
                onChange={(e) => setNewArea(e.target.value)}
                placeholder="e.g. Rooftop"
              />
            )}
          </Field>
        )}
        <RoomNote count={count} room={room} />
      </div>
    </Modal>
  )
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span aria-hidden className="mb-1.5 block text-sm font-medium text-muted">
        {label}
      </span>
      {children}
    </div>
  )
}

/** First time: start with a number of tables. */
function Starter({
  room,
  limit,
  busy,
  onCreate,
  onOne,
}: {
  room: number
  limit: number | null
  busy: boolean
  onCreate: (count: number, seats: number, areaName: string) => Promise<boolean>
  onOne: () => void
}) {
  const [count, setCount] = useState(Math.min(10, room))
  const [seats, setSeats] = useState(4)
  const [area, setArea] = useState('')
  const shown = Math.min(count, 5)
  return (
    <Card className="p-0">
      <div className="flex flex-col items-center gap-1.5 px-5 pt-8 pb-5 text-center">
        <IconTile area="kitchen" size="lg">
          <PicnicTable className="size-6" />
        </IconTile>
        <h3 className="mt-2 text-lg font-bold text-ink">Set up your tables</h3>
        <p className="max-w-md text-sm text-muted">
          Start with a number. You can rename, resize and move them to another area afterwards.
        </p>
      </div>
      <div className="flex flex-wrap items-end justify-center gap-4 px-5">
        <Labelled label="How many tables?">
          <NumberStepper label="How many tables?" value={count} max={100} onChange={setCount} />
        </Labelled>
        <Labelled label="Seats at each">
          <NumberStepper label="Seats at each" value={seats} max={MAX_SEATS} onChange={setSeats} />
        </Labelled>
        <Field label="Area (optional)" className="w-48">
          {(id) => (
            <Input
              id={id}
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="e.g. Main hall"
            />
          )}
        </Field>
      </div>
      <div className={cx('mx-4 mt-5 p-4 sm:mx-6', FLOOR, 'shadow-none')}>
        <div className="mb-3 text-section text-subtle">You will get</div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-4 opacity-60">
          {Array.from({ length: shown }, (_, i) => (
            <TableShape key={i} seats={seats} tone="plain">
              <span className="text-[15px] font-bold text-ink">T{i + 1}</span>
              <span className="text-xs text-muted">{seats} seats</span>
            </TableShape>
          ))}
          {count > shown && <span className="text-sm font-semibold text-muted">… to T{count}</span>}
        </div>
      </div>
      <div className="flex flex-col items-center gap-2.5 px-5 pt-5 pb-7">
        <Button
          size="lg"
          loading={busy}
          disabled={count > room}
          onClick={() => void onCreate(count, seats, area.trim())}
        >
          Create {count} {count === 1 ? 'table' : 'tables'}
        </Button>
        <button
          type="button"
          onClick={onOne}
          className="text-sm font-semibold text-primary hover:underline"
        >
          or add them one at a time
        </button>
        <RoomNote count={count} room={room} />
        {limit !== null && (
          <span className="text-xs text-muted">Your plan allows {limit} tables.</span>
        )}
      </div>
    </Card>
  )
}
